import mixpanel from "mixpanel-browser";
import { LOGIN_TYPE } from "../../enums/enums";
import { env } from "../../env";
import { WindowConfig } from "../../types";
import { getProject, getUser } from "../../utils/local-storage";
import { isNewUI } from "../../v2/flag";

export const windowWithType = window as unknown as WindowConfig;

const mixpanelToken = env.REACT_APP_MIXPANEL_TOKEN;

if (mixpanelToken) {
  mixpanel.init(mixpanelToken, {
    ignore_dnt: true,
    record_sessions_percent: 100,
    record_mask_text_selector: "none",
    secure_cookie: true,
    cross_subdomain_cookie: false,
    persistence: 'localStorage',
    opt_out_tracking_by_default: false,
    cookie_domain: window.location.hostname,
    // A page view on every route change, query string included, so runs and filters are visible
    track_pageview: "url-with-path-and-query-string"
  });

  // Switching UI reloads the app, so registering once at startup keeps every event tagged correctly
  mixpanel.register({ ui_version: isNewUI() ? "v2" : "classic" });
  registerTheme();
  // Read storage directly: utils/local-storage imports this module, so its getProject() may not be initialized yet
  registerCurrentProject(JSON.parse(localStorage.getItem("project") || "{}"));
}

/**
 * Keep the selected project as the `projectId` super property, so page views, session recordings and
 * every other automatic event carry the `projectId` group key, not only events sent through trackEvent()
 */
/**
 * The theme on every event: `theme` is the choice (system, light or dark; system until someone picks),
 * `theme_shown` what the page looks like. The classic UI is light whatever the choice.
 */
export function registerTheme() {
  if (!mixpanelToken) return;
  const stored = localStorage.getItem("mt-theme");
  const theme = stored === "dark" || stored === "light" ? stored : "system";
  const dark = theme === "dark" || (theme === "system" && window.matchMedia?.("(prefers-color-scheme: dark)").matches);
  mixpanel.register({ theme, theme_shown: isNewUI() && dark ? "dark" : "light" });
}

function registerCurrentProject(project: { id?: string } = getProject()) {
  if (project.id) {
    mixpanel.register({ projectId: project.id });
  }
}

export async function trackEvent(event: string, data?: object) {
  const project = getProject();

  //Mixpanel
  mixpanel.track(event, {
    ...data,
    projectId: project.id
  });

  // Gist
  windowWithType.gist && windowWithType.gist.track(event, { ...data, projectId: project.id });

  // Customer.io
  if (windowWithType._cio) {
    windowWithType._cio.track(event, data);
  }
}

/**
 * A person is identified by their email everywhere: here, and as distinct_id on backend events
 * (analytics-events Lambda), so web and backend events join on one user.
 * Do not identify by the Microtica user id or a git account id, the backend never sends those as distinct_id.
 */
export const handleTrackLoginEvent = ({ username, type }: { username: string, type: LOGIN_TYPE }) => {
  // Mixpanel
  // people.set() & identify() should be called together, only on Login
  mixpanel.identify(username);
  const user = getUser();
  mixpanel.people.set({
    "$email": username,
    "$last_login": new Date(),
    // the profile is stored right before login is tracked; the id is kept for lookups, not for identity
    ...(user.id && user.email === username && { microtica_user_id: user.id })
  });

  // Gist
  if (windowWithType.gist) {
    windowWithType.gist.identify({
      email: username
    });
  }

  // Customer.io
  if (windowWithType._cio) {
    windowWithType._cio.identify({
      id: username,
      email: username
    });
  }

  trackEvent("user_login", { email: username, type });
}

/**
 * do not use data from getProject() because the user does not have a project initialized yet when this function is called
 * even if it returns a value, it might be from the project from the previous logged in user
 */
export const handleTrackSignUpEvent = ({ userId, username, type, gitAccountId }: { userId: string, username: string, type: LOGIN_TYPE, gitAccountId?: string }) => {
  // Mixpanel
  // identify() links the anonymous visits before signup to the email in both Original and Simplified ID Merge.
  // alias() is not used: Simplified ID Merge ignores it, and the backend sends the email, never these ids, as distinct_id.
  mixpanel.identify(username);
  mixpanel.people.set({
    "$email": username,
    microtica_user_id: userId,
    ...(type === "github" && gitAccountId && { github_user_id: gitAccountId })
  });

  // Gist
  if (windowWithType.gist) {
    windowWithType.gist.identify({
      email: username
    });
  }

  // Customer.io
  if (windowWithType._cio) {
    windowWithType._cio.identify({
      id: username,
      email: username
    });
  }

  // no need to call trackEvent function because this event is tracked from backend
}

export const handleTrackLogoutEvent = () => {
  mixpanel.reset();
  // reset() clears super properties too; the UI version still applies to the next session on this page
  mixpanel.register({ ui_version: isNewUI() ? "v2" : "classic" });
  registerTheme();
}

/**
 * Add and register user to groups
 */
export const handleSetMixpanelUserGroups = (projectIds: string[]) => {
  // set_group() replaces the person's whole list, so no add_group(): that one appends to the projectId
  // super property, which registerCurrentProject() keeps as a single id, and crashes on it (old_values.push).
  // Analytics must never break the caller, which is often the projects query.
  try {
    mixpanel.set_group("projectId", projectIds);  // register the user in the groups
    // set_group() also registers all of the user's projects as the projectId super property;
    // events should count for the project the user is in, so put the selected one back
    registerCurrentProject();
  } catch (error) {
    console.warn("Mixpanel project groups weren't updated", error);
  }
}

/**
 * Update projectId group profile
 */
export const handleSetMixpanelGroup = () => {
  const project = getProject();
  registerCurrentProject();
  // Mixpanel group profile
  mixpanel.get_group("projectId", project.id).set({
    "projectId": project.id,
    "projectName": project.name,
    "projectDescription": project.description
  });
}
